import { clsx, type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"

// tailwind-merge only knows the stock t-shirt shadow scale, so it files our
// `shadow-{side}-{level}` tokens under shadow-*colour* and lets them coexist
// with `shadow`/`shadow-md` — two box-shadows emitted, stylesheet order
// deciding the winner. Registering the scale puts them in the same group so the
// last one wins, as with any other utility.
const SHADOW_TOKEN_SCALE = [
  "top-050",
  "top-100",
  "top-150",
  "bottom-050",
  "bottom-100",
  "bottom-150",
  "left-050",
  "left-100",
  "left-150",
  "right-050",
  "right-100",
  "right-150",
]

// `blur-*` / `backdrop-blur-*` validate against
// the t-shirt sizes or an arbitrary value, and a bare `100` is neither — so
// two backdrop-filters survive the merge and stylesheet order picks the winner.
const BLUR_TOKEN_SCALE = ["100", "200", "1200"]

// style-config's `font-{size}-{weight}` typography utilities set font-size,
// line-height, weight, family, letter-spacing, style, stretch and decoration.
// tailwind-merge files them under font-family, so `text-sm font-medium` survive
// alongside them — and v4 sorts the single-property utilities after them, so
// those win. Grouping them drops the earlier conflicting utilities instead.
// `leading-*` is left out: style-config emits it after the tokens on purpose.
const TYPOGRAPHY_TOKENS = [
  "inherit",
  ...[
    "xsmall",
    "small",
    "medium",
    "large",
    "xlarge",
    "2xlarge",
    "3xlarge",
    "4xlarge",
  ].flatMap((size) =>
    ["400", "525", "650"].map((weight) => `${size}-${weight}`)
  ),
]

const twMerge = extendTailwindMerge<"typography">({
  extend: {
    classGroups: {
      shadow: [{ shadow: SHADOW_TOKEN_SCALE }],
      blur: [{ blur: BLUR_TOKEN_SCALE }],
      "backdrop-blur": [{ "backdrop-blur": BLUR_TOKEN_SCALE }],
      typography: [{ font: TYPOGRAPHY_TOKENS }],
    },
    conflictingClassGroups: {
      typography: [
        "font-size",
        "font-weight",
        "font-family",
        "tracking",
        "font-style",
        "font-stretch",
        "text-decoration",
      ],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function slugFromPath(path: string) {
  return path.replace("/src/content/", "").replace(".md", "")
}

export function hexToHsl(hex: string): [number, number, number] {
  if (hex) {
    const sanitizedHex = hex.replace("#", "")

    const red = parseInt(sanitizedHex.substring(0, 2), 16)
    const green = parseInt(sanitizedHex.substring(2, 4), 16)
    const blue = parseInt(sanitizedHex.substring(4, 6), 16)

    const normalizedRed = red / 255
    const normalizedGreen = green / 255
    const normalizedBlue = blue / 255

    const max = Math.max(normalizedRed, normalizedGreen, normalizedBlue)
    const min = Math.min(normalizedRed, normalizedGreen, normalizedBlue)

    let hue, saturation, lightness

    if (max === min) {
      hue = 0
    } else if (max === normalizedRed) {
      hue = ((normalizedGreen - normalizedBlue) / (max - min)) % 6
    } else if (max === normalizedGreen) {
      hue = (normalizedBlue - normalizedRed) / (max - min) + 2
    } else {
      hue = (normalizedRed - normalizedGreen) / (max - min) + 4
    }

    hue = Math.round(hue * 60)

    if (hue < 0) {
      hue += 360
    }

    lightness = (max + min) / 2

    if (max === min) {
      saturation = 0
    } else if (lightness <= 0.5) {
      saturation = (max - min) / (max + min)
    } else {
      saturation = (max - min) / (2 - max - min)
    }

    saturation = Math.round(saturation * 100)
    lightness = Math.round(lightness * 100)

    return [hue, saturation, lightness]
  }
  return [0, 0, 0]
}

export function hexToRgb(hex: string): [number, number, number] {
  if (hex) {
    const sanitizedHex = hex.replace("#", "")

    const red = parseInt(sanitizedHex.substring(0, 2), 16)
    const green = parseInt(sanitizedHex.substring(2, 4), 16)
    const blue = parseInt(sanitizedHex.substring(4, 6), 16)

    return [red, green, blue]
  }
  return [0, 0, 0]
}
